import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  GraduationCap,
  PlayCircle,
  Maximize,
  ArrowLeft,
  ExternalLink,
  ListOrdered,
  Link as LinkIcon,
  Settings2,
  AlertTriangle,
} from 'lucide-react'
import { usePageBreadcrumbs } from '../context/BreadcrumbContext'
import useLearningEditor from '../hooks/useLearningEditor'
import {
  enterFullscreen,
  fetchLearningCatalog,
  findItem,
  hubUrl,
  OPENING_FRAME_SECONDS,
  visibleCatalog,
  withOpeningFrame,
} from '../utils/learning'

/* -------------------------------------------------------------------------- */
/* States                                                                     */
/* -------------------------------------------------------------------------- */

function CatalogSkeleton() {
  return (
    <div className="flex flex-col gap-5 lg:flex-row" aria-busy="true" aria-label="Loading">
      <div className="flex flex-col gap-2 lg:w-56">
        {[0, 1, 2].map((i) => <div key={i} className="skeleton h-9 rounded-lg" />)}
      </div>
      <div className="grid flex-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
            <div className="skeleton mb-3 aspect-video rounded-lg" />
            <div className="skeleton mb-2 h-4 w-3/4 rounded" />
            <div className="skeleton h-3 w-1/3 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

function Notice({ icon: Icon = GraduationCap, title, children, tone = 'default', action }) {
  const tile = tone === 'error'
    ? 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300'
    : 'bg-primary-50 text-primary-600 dark:bg-primary-700/30 dark:text-primary-300'
  return (
    <div
      className="animate-card-in flex flex-col items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-6 py-11 text-center shadow-sm dark:border-gray-700 dark:bg-gray-900"
      role={tone === 'error' ? 'alert' : undefined}
    >
      <div className={`mb-2 flex h-14 w-14 items-center justify-center rounded-full ${tile}`}>
        <Icon className="h-6 w-6" strokeWidth={2} aria-hidden="true" />
      </div>
      <p className="m-0 text-[15px] font-semibold text-gray-900 dark:text-gray-100">{title}</p>
      {children && <p className="m-0 max-w-[420px] text-sm leading-5 text-gray-500 dark:text-gray-400">{children}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Catalog view                                                               */
/* -------------------------------------------------------------------------- */

function CategoryNav({ categories, selected, onSelect }) {
  return (
    <nav aria-label="Learning categories" className="flex gap-2 overflow-x-auto pb-1 lg:w-56 lg:flex-shrink-0 lg:flex-col lg:overflow-visible">
      {categories.map((category) => {
        const active = category.id === selected?.id
        return (
          <button
            key={category.id}
            type="button"
            onClick={() => onSelect(category)}
            aria-current={active ? 'true' : undefined}
            className={`flex items-center justify-between gap-3 whitespace-nowrap rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
              active
                ? 'bg-navy-800 text-white shadow-sm dark:bg-navy-700'
                : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-200 dark:ring-gray-700 dark:hover:bg-gray-800'
            }`}
          >
            <span className="truncate">{category.name}</span>
            <span className={`text-xs ${active ? 'text-white/70' : 'text-gray-400'}`}>{category.items.length}</span>
          </button>
        )
      })}
    </nav>
  )
}

function PlaceholderThumb() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-navy-700 to-navy-900 text-white">
      <PlayCircle className="h-10 w-10 opacity-90 transition-transform group-hover:scale-110" strokeWidth={1.5} aria-hidden="true" />
    </div>
  )
}

// The card's thumbnail is the video's own opening frame: a muted, metadata-only
// <video> that seeks to OPENING_FRAME_SECONDS via a media fragment. Nothing is
// stored; if the browser can't load the video the gradient placeholder shows.
function VideoThumb({ src }) {
  const [failed, setFailed] = useState(false)
  if (!src || failed) return <PlaceholderThumb />
  return (
    <div className="relative h-full w-full bg-gray-900">
      <video
        src={withOpeningFrame(src)}
        muted
        playsInline
        preload="metadata"
        tabIndex={-1}
        aria-hidden="true"
        onError={() => setFailed(true)}
        className="pointer-events-none h-full w-full object-cover"
        data-testid="learning-card-preview"
      />
      <div className="absolute inset-0 flex items-center justify-center bg-black/15 text-white transition-colors group-hover:bg-black/25">
        <PlayCircle className="h-11 w-11 drop-shadow-md transition-transform group-hover:scale-110" strokeWidth={1.5} aria-hidden="true" />
      </div>
    </div>
  )
}

function ItemCard({ item, category, index, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen(item)}
      className="animate-card-in group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-sm transition-shadow duration-200 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 dark:border-gray-700 dark:bg-gray-900"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="p-2 pb-0">
        <div className="aspect-video w-full overflow-hidden rounded-lg">
          <VideoThumb src={hubUrl(item.video_url)} />
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3 className="m-0 text-sm font-semibold leading-5 text-gray-900 dark:text-gray-100">{item.title}</h3>
        {item.description && (
          <p className="m-0 line-clamp-2 text-xs leading-5 text-gray-500 dark:text-gray-400">{item.description}</p>
        )}
        <span className="mt-auto inline-flex w-fit items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {category.name}
        </span>
      </div>
    </button>
  )
}

function CatalogView({ categories, onOpen }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const slug = searchParams.get('category')
  const selected = categories.find((c) => c.slug === slug) || categories[0]

  return (
    <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <CategoryNav
        categories={categories}
        selected={selected}
        onSelect={(category) => setSearchParams({ category: category.slug }, { replace: true })}
      />
      <section aria-label={selected.name} className="min-w-0 flex-1">
        {selected.description && (
          <p className="m-0 mb-3 text-sm text-gray-500 dark:text-gray-400">{selected.description}</p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {selected.items.map((item, i) => (
            <ItemCard key={item.id} item={item} category={selected} index={i} onOpen={onOpen} />
          ))}
        </div>
      </section>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Item view                                                                  */
/* -------------------------------------------------------------------------- */

function VideoPlayer({ src, title }) {
  const videoRef = useRef(null)
  const started = useRef(false)
  if (!src) return null
  // The #t fragment shows the opening frame before Play instead of black;
  // the first Play rewinds to the very start so nothing is skipped.
  const onPlay = () => {
    const el = videoRef.current
    if (started.current || !el) return
    started.current = true
    if (el.currentTime > 0 && el.currentTime <= OPENING_FRAME_SECONDS + 0.05) el.currentTime = 0
  }
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-black shadow-sm dark:border-gray-700">
      <video
        ref={videoRef}
        src={withOpeningFrame(src)}
        controls
        playsInline
        preload="metadata"
        onPlay={onPlay}
        className="aspect-video w-full bg-black"
        aria-label={title}
      >
        Your browser can't play this video.
      </video>
      <div className="flex justify-end border-t border-gray-800 bg-gray-950 px-3 py-2">
        <button
          type="button"
          onClick={() => enterFullscreen(videoRef.current)}
          className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-gray-200 hover:bg-white/10"
        >
          <Maximize className="h-3.5 w-3.5" aria-hidden="true" />
          Full screen
        </button>
      </div>
    </div>
  )
}

function ItemView({ item, category }) {
  return (
    <article className="flex flex-col gap-5">
      <div>
        <Link
          to={`/learning?category=${encodeURIComponent(category.slug)}`}
          className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          {category.name}
        </Link>
        <h2 className="m-0 text-xl font-bold leading-7 text-gray-900 dark:text-gray-100">{item.title}</h2>
        <span className="mt-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {category.name}
        </span>
        {item.description && (
          <p className="m-0 mt-3 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">{item.description}</p>
        )}
      </div>

      <div className="max-w-4xl">
        <VideoPlayer key={item.id} src={hubUrl(item.video_url)} title={item.title} />
      </div>

      {item.steps?.length > 0 && (
        <section className="max-w-4xl rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="m-0 mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <ListOrdered className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Step by step
          </h3>
          <ol className="m-0 flex list-none flex-col gap-3 p-0">
            {item.steps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm leading-6 text-gray-700 dark:text-gray-300">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-navy-50 text-xs font-semibold text-navy-700 dark:bg-navy-900/40 dark:text-navy-200">
                  {i + 1}
                </span>
                <span className="whitespace-pre-line">{step}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {item.links?.length > 0 && (
        <section className="max-w-4xl rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <h3 className="m-0 mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <LinkIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
            Useful links
          </h3>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {item.links.map((link, i) => (
              <li key={i}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:underline dark:text-navy-300"
                >
                  {link.label || link.url}
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </article>
  )
}

/* -------------------------------------------------------------------------- */
/* Page                                                                       */
/* -------------------------------------------------------------------------- */

export default function Learning() {
  const { itemId } = useParams()
  const navigate = useNavigate()
  const editor = useLearningEditor()
  const [catalog, setCatalog] = useState(null)
  const [error, setError] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    fetchLearningCatalog({ signal: controller.signal })
      .then(setCatalog)
      .catch((err) => { if (err?.name !== 'AbortError') setError(err) })
    return () => controller.abort()
  }, [reloadKey])

  const categories = useMemo(() => visibleCatalog(catalog), [catalog])
  const found = itemId ? findItem(categories, itemId) : null
  const loading = !catalog && !error

  usePageBreadcrumbs(itemId && found ? [{ label: found.item.title }] : null)

  const openItem = useCallback((item) => navigate(`/learning/${item.id}`), [navigate])

  let body
  if (error) {
    body = (
      <Notice
        icon={AlertTriangle}
        tone="error"
        title="Learning content is unavailable right now"
        action={(
          <button
            type="button"
            onClick={() => { setCatalog(null); setReloadKey((k) => k + 1) }}
            className="text-sm font-semibold text-navy-700 hover:underline dark:text-navy-300"
          >
            Try again
          </button>
        )}
      >
        Please try again in a few minutes.
      </Notice>
    )
  } else if (loading) {
    body = <CatalogSkeleton />
  } else if (itemId && !found) {
    body = (
      <Notice
        title="This guide isn't available"
        action={<Link to="/learning" className="text-sm font-semibold text-navy-700 hover:underline dark:text-navy-300">See all guides</Link>}
      >
        It may have been moved or removed.
      </Notice>
    )
  } else if (found) {
    body = <ItemView item={found.item} category={found.category} />
  } else if (categories.length === 0) {
    body = (
      <Notice title="No learning content yet">
        How-to videos and step-by-step guides will appear here as soon as they are published.
      </Notice>
    )
  } else {
    body = <CatalogView categories={categories} onOpen={openItem} />
  }

  return (
    <div className="animate-page-in flex max-w-[1200px] flex-col gap-5">
      <header className="animate-header-in flex flex-wrap items-center gap-3.5">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm">
          <GraduationCap className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="m-0 text-2xl font-bold leading-[30px] tracking-tight text-gray-900 dark:text-gray-100">
            Learning
          </h1>
          <p className="m-0 mt-1 text-sm text-gray-500 dark:text-gray-400" style={{ textWrap: 'pretty' }}>
            How-to videos and step-by-step guides for using Xtract.
          </p>
        </div>
        {editor && (
          <Link
            to="/learning/manage"
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <Settings2 className="h-4 w-4" aria-hidden="true" />
            Manage
          </Link>
        )}
      </header>
      {body}
    </div>
  )
}
